import axios from "axios";
import { BASE_URL } from "../config/config";
import { getToken, getCSRFToken, getUserRole, getUserLevel, getUserId } from "../helpers/index";

const instance = axios.create({
  baseURL: BASE_URL,
});

instance.interceptors.request.use(
  (config) => {
    const token = getToken();
    const CSRF_TOKEN = getCSRFToken();
    
    if (token) config.headers["Authorization"] = "Bearer " + token;
    
    if (CSRF_TOKEN) config.headers["X-XSRF-TOKEN"] = CSRF_TOKEN;
    
    const role = getUserRole();
    const userLevel = getUserLevel();
    const userId = getUserId();
    
    if (role) config.headers["X-User-Role"] = role;
    if (userLevel !== null && userLevel !== undefined) config.headers["X-User-Level"] = userLevel.toString();
    if (userId) config.headers["X-User-Id"] = userId.toString();
    
    config.withCredentials = true;
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (requestUrl && (requestUrl.includes('csrf') || requestUrl.includes('admin/profile') || requestUrl.includes('admin/refresh'))) {
        console.log('Auth check, refresh, or CSRF endpoint error (ignored for interceptor refresh):', requestUrl);
        return Promise.reject(error);
      }

      const errorMessage = error.response?.data?.message || '';
      const errorStatus = error.response?.data?.status || '';

      const isAuthError = 
        errorMessage.toLowerCase().includes('token') ||
        errorMessage.toLowerCase().includes('unauthorized') ||
        errorMessage.toLowerCase().includes('authentication') ||
        errorMessage.toLowerCase().includes('invalid') ||
        errorMessage.toLowerCase().includes('expired') ||
        errorStatus === 'expired';

      if (isAuthError) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (token) {
                originalRequest.headers['Authorization'] = 'Bearer ' + token;
              }
              return instance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          console.log('Attempting silent token refresh...');
          const response = await instance.post('admin/refresh');
          const newToken = response.data?.token;

          processQueue(null, newToken);
          isRefreshing = false;

          // Retry the original request
          if (newToken) {
            originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
          }
          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          isRefreshing = false;

          console.log('Refresh failed, logging out...', refreshError);
          window.location.href = '/';
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default instance;
